import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { fetchProject } from "../../../api/project";
import { ProjectDocumentArchive } from "../../../components/dashboard/projects/folders/project-document-archive";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useProjectPermissions } from "../../../routes/roleOnly";

export const ProjectArchive = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const projectPermissions = useProjectPermissions(project);
  const folderId = searchParams.get("folder");

  const handleFolderNavigate = useCallback(
    (nextFolderId) => {
      const current = searchParams.get("folder");
      const next = nextFolderId ? String(nextFolderId) : null;
      if ((current || null) === next) return;
      if (next) setSearchParams({ folder: next }, { replace: true });
      else setSearchParams({}, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      setLoadError(true);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await fetchProject(id);
        if (!cancelled) setProject(response?.data || null);
      } catch (_) {
        if (!cancelled) {
          setProject(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <ResourceLoading />;
  }

  if (loadError || !project?.id) {
    return (
      <div className="p-6 text-sm text-[#59616d]">
        {t("document_archive.not_found")}
      </div>
    );
  }

  if (!projectPermissions.canReadFolders) {
    return (
      <div className="p-6 text-sm text-[#59616d]">
        {t("document_archive.no_access")}
      </div>
    );
  }

  const projectLabel =
    project.displayTitle ||
    [project.project_number, project.name].filter(Boolean).join(" ") ||
    project.name ||
    "";

  return (
    <div className="flex h-[calc(100dvh-4rem)] min-h-0 flex-col overflow-hidden bg-[#F4F6F5]">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[#E5E9E8] bg-white px-3.5 sm:px-5">
        <Link
          to={`/dashboard/projects/${project.id}`}
          title={t("document_archive.back_to_project")}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-[#0F766E] text-white hover:bg-[#0B5A54]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        <div className="min-w-0 leading-[1.15]">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0F766E]">
            {t("document_archive.page_kicker")}
          </div>
          <div className="truncate text-[15px] font-semibold text-[#14171c]">
            {projectLabel}
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 p-3 sm:p-4">
        <ProjectDocumentArchive
          project={project}
          initialFolderId={folderId}
          canManage={projectPermissions.canManageFolders}
          isArchiveAdmin={projectPermissions.isAdmin}
          layout="page"
          onCurrentFolderChange={handleFolderNavigate}
        />
      </div>
    </div>
  );
};
